import { useState } from 'react';
import { scansApi, type DomainVerificationMethod } from '../api/scans.ts';

interface DomainVerificationPanelProps {
  scanId: string;
  targetUrl: string;
  verificationToken: string;
  domainVerifiedAt: string | null;
  domainVerificationMethod: DomainVerificationMethod | null;
  onVerified: () => void;
}

// Renders the "Verify domain ownership" panel on the scan results page. Two methods
// (DNS TXT or HTML meta tag) — the customer picks one, places the proof on their domain,
// then clicks "Check now". On success, the verifier page upgrades to show a green
// "domain ownership verified" badge.
//
// Already-verified scans render a compact confirmation block instead of the picker.
export default function DomainVerificationPanel({
  scanId,
  targetUrl,
  verificationToken,
  domainVerifiedAt,
  domainVerificationMethod,
  onVerified,
}: DomainVerificationPanelProps) {
  const [method, setMethod] = useState<DomainVerificationMethod>('DnsTxt');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const host = safeHost(targetUrl);
  const txtValue = `eaaproof-verify=${verificationToken}`;
  const metaTag = `<meta name="eaaproof-verify" content="${verificationToken}">`;

  async function handleVerify() {
    setBusy(true);
    setError(null);
    try {
      await scansApi.verifyDomain(scanId, method);
      onVerified();
    } catch (e: unknown) {
      // The API returns a 422 with a user-facing reason (no TXT record at X, etc.) —
      // surface it verbatim because it tells the customer what they need to fix.
      const msg = e instanceof Error ? e.message : 'Verification failed.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (domainVerifiedAt) {
    return (
      <section className="domain-verify verified" aria-labelledby="dv-heading">
        <h2 id="dv-heading">Domain ownership verified ✓</h2>
        <p className="muted small">
          Verified on {new Date(domainVerifiedAt).toLocaleDateString()}
          {domainVerificationMethod && ` via ${friendlyMethod(domainVerificationMethod)}`}.
          Your verifier page now shows a "domain ownership verified" badge.
        </p>
      </section>
    );
  }

  return (
    <section className="domain-verify" aria-labelledby="dv-heading">
      <h2 id="dv-heading">Verify domain ownership (optional)</h2>
      <p>
        Proving you control <strong>{host}</strong> upgrades your verifier page with a
        "domain ownership verified" badge. Two methods — pick whichever your hosting setup
        makes easier.
      </p>

      <div className="domain-verify-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={method === 'DnsTxt'}
          className={method === 'DnsTxt' ? 'tab-active' : ''}
          onClick={() => setMethod('DnsTxt')}
        >
          DNS TXT record
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={method === 'MetaTag'}
          className={method === 'MetaTag' ? 'tab-active' : ''}
          onClick={() => setMethod('MetaTag')}
        >
          HTML meta tag
        </button>
      </div>

      {method === 'DnsTxt' && (
        <div className="domain-verify-pane">
          <p>Add this TXT record to <strong>{host}</strong> at your DNS provider:</p>
          <CopyableCode label="TXT value" value={txtValue} />
          <p className="muted small">
            We check both the exact host and the apex domain, so a TXT on either works.
            DNS propagation usually takes 1-5 minutes.
          </p>
        </div>
      )}

      {method === 'MetaTag' && (
        <div className="domain-verify-pane">
          <p>Add this tag inside the <code>&lt;head&gt;</code> of <strong>{targetUrl}</strong>:</p>
          <CopyableCode label="HTML" value={metaTag} />
          <p className="muted small">
            Quicker than DNS — but the tag must be in the rendered HTML returned to a crawler,
            not injected later by JavaScript.
          </p>
        </div>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      <button
        type="button"
        className="cta"
        onClick={handleVerify}
        disabled={busy}
      >
        {busy ? 'Checking…' : 'Check now'}
      </button>
    </section>
  );
}

function CopyableCode({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — user can manually copy.
    }
  }
  return (
    <div className="copyable">
      <label className="muted small">{label}</label>
      <code className="copyable-value">{value}</code>
      <button type="button" className="cta tertiary" onClick={handleCopy}>
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
    </div>
  );
}

function safeHost(targetUrl: string): string {
  try { return new URL(targetUrl).host; } catch { return targetUrl; }
}

function friendlyMethod(m: DomainVerificationMethod): string {
  return m === 'DnsTxt' ? 'DNS TXT record' : 'HTML meta tag';
}
